import { memo } from 'preact/compat';
import { useRef, useCallback } from 'preact/hooks';
import { useReorderableItem } from '../hooks/PRIVATE_useReorderable';
import { ReorderableItemProps } from '../UNSAFE_Collection/Reorderable.types';
import { LIST_ITEM_SELECTOR } from '../PRIVATE_List';
import { classNames } from '../utils/UNSAFE_classNames';
import { styles } from './themes/ListViewStyles.css';
import { isKeyDefined } from '../utils/UNSAFE_keys';

export type ListReorderableItemProps<K> = ReorderableItemProps<K>;

/**
 * A private item that will be used by ReorderableCardFlexView, location TBD (could be moved to PRIVATE_CardFlexView)
 */
export function ListViewReorderableItem<K extends string | number>({
  children,
  dragKey,
  setDragKey,
  onReorder,
  rootRef,
  itemKey
}: ListReorderableItemProps<K> & { itemKey: K }) {
  const itemRef = useRef<HTMLDivElement | null>(null);
  const itemStyles = {
    itemOverlayFront: styles.reorderableItemOverlayFront,
    itemOverlayRear: styles.reorderableItemOverlayRear,
    itemDragger: styles.reorderableItemDragger
  };
  const getItem = () => itemRef?.current?.closest(LIST_ITEM_SELECTOR);
  const setTransferData = useCallback(
    (event: DragEvent) => {
      event.dataTransfer?.setData('text/listview-items-key', JSON.stringify(itemKey));
    },
    [itemKey]
  );
  const { rootProps, isDragged, overlayZoneBeforeProps, overlayZoneAfterProps } =
    useReorderableItem({
      isDisabled: onReorder ? false : true,
      dragKey,
      setDragKey,
      onReorder,
      rootRef,
      ref: itemRef,
      getItem,
      setTransferData,
      itemStyles,
      itemSelector: LIST_ITEM_SELECTOR,
      isVertical: true
    });
  const classes = classNames([rootProps?.class, styles.reorderableItemDnd]);

  return (
    <div {...rootProps} class={classes}>
      {children}
      {!isDragged && isKeyDefined(dragKey) && (
        <>
          <div {...overlayZoneBeforeProps}></div>
          <div {...overlayZoneAfterProps}></div>
        </>
      )}
    </div>
  );
}

export const MemoizeListViewReorderableItem = memo(
  ListViewReorderableItem,
  <K,>(prev: ListReorderableItemProps<K>, next: ListReorderableItemProps<K>) => {
    if (prev && next) {
      return prev.children === next.children && prev.dragKey === next.dragKey;
    }
    return false;
  }
);
